import React, { useState } from 'react'
import classNames from '../../utils/classNames'
import { Menu } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/24/solid'

const classes = {
  base: 'focus:outline-none transition ease-in-out duration-100 open-sans line-height-inherit',
  disabled: 'opacity-30 cursor-not-allowed pointer-events-none',
  pill: 'rounded-full',
  size: {
    'no-size': 'p-0 py-2 text-mdbase',
    small: 'px-2.5 py-1.5 text-md',
    normal: 'px-4 py-2.5 text-mdbase',
    large: 'px-5 py-3 text-base',
  },
  shadow: {
    small: 'shadow-sm',
    medium: 'shadow-md',
    large: 'shadow-lg',
  },
  variant: {
    // add enabled: for hover with tailwind v3
    green: 'bg-green-500 hover:bg-green-600 text-white',
    gray: 'bg-gray-200 hover:bg-gray-300 text-black',
    red: 'bg-red-500 hover:bg-red-600 text-white',
    redOutline: 'hover:bg-red-50 border border-red-500 text-red-600 hover:text-red-500',
    pink: 'bg-red-300 hover:bg-red-400 text-black',
    blue: 'bg-blue-500 hover:bg-blue-600 text-white',
    yellow: 'bg-yellow hover:bg-orange text-white',
    yellowOutline: 'hover:bg-yellow border-2 border-yellow text-orange hover:text-white',
    white: 'bg-white hover:bg-gray-75 text-black border border-gray-300 hover:border-gray-400',
    black: 'bg-black hover:bg-gray-800 text-white',
    link: 'bg-white border-none hover:underline',
    greenOutline: 'border border-gray-300 hover:border-green-500'
  },
}

interface ComponentProps {
  children?: any
  className?: string
  style?: object
  variant?:
  | "black"
  | "green"
  | "gray"
  | "red"
  | "blue"
  | "yellow"
  | "yellowOutline"
  | "none"
  | "white"
  | "pink"
  | "link"
  | "greenOutline"
  | string
  size?: "small" | "normal" | "large" | "no-size" | string
  pill?: boolean
  disabled?: boolean
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => any
  type?: "button" | "submit" | "reset"
  icon?: any
  ref?: any
  shadow?: "small" | "medium" | "large"
  id?: string
  withDropdown?: { label: string; icon?: string; onClick: () => void }[]
  dataTestId?: string
  hoverBg?: string
  hoverText?: string
  autoFocus?: boolean
}

const Button = ({
  children,
  className,
  style,
  variant = "gray",
  size = "normal",
  shadow,
  pill,
  disabled = false,
  onClick,
  type,
  icon,
  ref,
  withDropdown,
  dataTestId,
  hoverBg,
  hoverText,
  autoFocus,
}: ComponentProps) => {
  const [hoverStyles, setHoverStyles] = useState<React.CSSProperties>({})

  return (
    <>
      <button
        data-testid={dataTestId}
        disabled={disabled}
        onClick={onClick}
        className={classNames(
          classes.base ? classes.base : "",
          withDropdown ? "rounded-l-md" : "rounded-md",
          classes.size[size] ? classes.size[size] : "",
          classes.variant[variant] ? classes.variant[variant] : "",
          pill ? (withDropdown ? "rounded-l-full" : classes.pill) : "",
          disabled ? classes.disabled : "",
          classes.shadow[shadow] ? classes.shadow[shadow] : "",
          className ? className : "",
          icon ? "flex justify-center items-center" : "",
        )}
        style={{ ...style, ...hoverStyles }}
        type={type || "button"}
        onMouseEnter={() =>
          (hoverBg?.length || hoverText?.length) &&
          setHoverStyles({
            ...(hoverBg?.length && { background: hoverBg }),
            ...(hoverText?.length && { color: hoverText }),
          })
        }
        onMouseLeave={() => (hoverBg?.length || hoverText?.length) && setHoverStyles({})}
        ref={ref}
        autoFocus={autoFocus}
      >
        {icon && <span className="mr-2 -ml-0.5">{icon}</span>}
        {children}
      </button>
      {withDropdown && (
        <Menu as="div" className="relative -ml-px block">
          <Menu.Button
            className={classNames(
              `relative inline-flex items-center rounded-r-md bg-white px-2 text-gray-400 hover:bg-gray-50 cursor-pointer focus:z-10`,
              classes.base && classes.base,
              // classes.size[size] && classes.size[size],
              "py-2.5 text-mdbase",
              classes.variant[variant] && classes.variant[variant],
              pill && "rounded-r-full",
              disabled && classes.disabled,
              classes.shadow[shadow] && classes.shadow[shadow],
              // className && className,
              // icon && 'flex justify-center items-center'
            )}
          >
            <span className="sr-only">Open options</span>
            <ChevronDownIcon aria-hidden="true" className="h-6 w-6" />
          </Menu.Button>
          <Menu.Items
            // transition
            className={classNames(
              "absolute right-0 z-10 -mr-1 mt-2 w-56 rounded-md origin-top-right bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in",
            )}
          >
            <div className="py-1">
              {withDropdown.map((item) => (
                <Menu.Item key={item.label}>
                  <a
                    onClick={item.onClick}
                    className="block px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 cursor-pointer data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                  >
                    {item.label}
                  </a>
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </Menu>
      )}
    </>
  )
}

export default Button
